/**
 * Account 관련 API 함수들
 * - 사용자 인증 (회원가입, 로그인)
 * - 결제 상태
 * - 프로세스 로그
 * - 실행 상태
 * - 시간 기반 규칙 (TimeRules)
 */

import { AuthContextType } from "../features/auth/AuthContext";
import { apiWithTimeout } from "../shared/api/timeout";
import axios from 'axios';
import {
    apiHost,
    getErrorMessage,
    isApiConnectionFailure,
} from "../shared/api/helpers";
import type { ApiErrorSetter } from "../shared/api/helpers";

//-----------------------------------------------------------------------------
// User Account
//-----------------------------------------------------------------------------

export interface CreateUserOpts {
    auth: AuthContextType,
    payload: {
        username: string,
        password: string,
        password_confirm: string,
        bus_num: string,
        rep_name: string,
        bus_started_at: string,
    },
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: (arg: any) => void;
    setError?: (...arg: any) => void;
}

export async function createUser(opts: CreateUserOpts) {
    const { payload, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        setErrorMessage && setErrorMessage(null); // Reset error state
        setProcessMessage && setProcessMessage("회원가입 중입니다..."); // Set loading state
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/account/signup/`, payload);

        if (response.status !== 200) {
            // If not 200, throw error with response data
            throw { response };
        }

        if (response.data && setResult) {
            setResult(response.data);
        }

    } catch (error: any) {
        let message = "네트워크 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response?.data;

        if (error.code === 'ECONNABORTED') {
            message = "요청 시간이 초과되었습니다. 다시 시도해주세요.";
        }

        if (errorData?.errors && typeof errorData.errors === 'object' && setError) {
            for (const [key, value] of Object.entries(errorData.errors)) {
                const fieldMessage = Array.isArray(value) ? value[0] : value;
                setError(key, { type: 'custom', message: fieldMessage });
            }
            return;
        }

        if (Array.isArray(errorData?.errors) && errorData.errors.length > 0) {
            message = errorData.errors[0];
        } else if (errorData?.message) {
            message = errorData.message;
        }

        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    } finally {
        setProcessMessage && setProcessMessage("");
    }

}


export interface CheckUserNameOpts {
    username: string
}
export async function checkUsername(opts: CheckUserNameOpts) {
    const { username } = opts;
    try {
        const payload = { username }
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/account/check_username/`, payload);
        return response
    } catch (error) {

    }
}

export interface CheckBusinessOpts {
    bus_num: string
}
export async function checkBusiness(opts: CheckBusinessOpts) {
    const { bus_num } = opts;
    try {
        const payload = { bus_num }
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/account/check_business/`, payload);
        return response
    } catch (error) {

    }
}

export interface ValidateBusinessOpts {
    bus_num: string;
    rep_name: string;
    bus_started_at: string;
    b_adr?: string;
}

export async function validateBusiness(opts: ValidateBusinessOpts) {
    const { bus_num, rep_name, bus_started_at, b_adr } = opts;
    try {
        const params = new URLSearchParams({
            bus_num,
            rep_name,
            bus_started_at
        });
        if (b_adr) {
            params.set('b_adr', b_adr);
        }
        // 인증이 필요없는 별도의 axios 인스턴스 사용
        const response = await axios.get(`${apiHost}/api/account/verify_business?${params.toString()}`, {
            timeout: 10000 // 10초 타임아웃
        });
        return response;
    } catch (error) {
        throw error;
    }
}

//-----------------------------------------------------------------------------
// Subscription Status
//-----------------------------------------------------------------------------
export async function fetchSubscriptionStatus(opts: {
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/subscription/status/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
        return response.data;
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
        throw error;
    }
}

/**
 * 사용자의 활성 결제 상태 확인
 */
export async function checkUserActiveSubscription(opts: {
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/subscription/active/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
        return response.data;
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
        throw error;
    }
}

/**
 * 사용자의 결제 내역 조회
 */
export async function fetchUserSubscriptions(opts: {
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/subscriptions/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
        return response.data;
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
        throw error;
    }
}

//-----------------------------------------------------------------------------
// Process Readiness
//-----------------------------------------------------------------------------
export interface ProcessReadinessError {
    code: string;
    message: string;
}

export interface ProcessReadiness {
    ready: boolean;
    errors: ProcessReadinessError[];
}

export async function fetchProcessReadiness(): Promise<ProcessReadiness | null> {
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/process-readiness/`);
        return response.data;
    } catch (error) {
        console.error('Process readiness fetch failed:', error);
        return null;
    }
}

//-----------------------------------------------------------------------------
// IsRunning
//-----------------------------------------------------------------------------
export async function fetchUserIsRunning(opts: {
    setResult?: (arg: any) => void
}) {
    const { setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/running/`);
        if (response.data && setResult) {
            setResult(response.data.is_running);
        }
    } catch (error: any) {
        const message = getErrorMessage(error);
        console.error(message);
    }
}

export async function toggleUserIsRunning(opts: {
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/account/running/toggle/`);
        if (response.data && setResult) {
            setResult(response.data.is_running);
        }
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}


//-----------------------------------------------------------------------------
// UserConfig
//-----------------------------------------------------------------------------
export async function retrieveUserConfig(opts: {
    auth: AuthContextType,
    setErrorMessage?: ApiErrorSetter,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/userconfig/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message, isApiConnectionFailure(error));
        } else {
            console.error(message);
        }
    }
}


export async function updateUserConfig(opts: {
    payload: any,
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setSuccessMessage?: (arg: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { payload, setErrorMessage, setProcessMessage, setSuccessMessage } = opts;
    try {
        await apiWithTimeout.normal.post(`${apiHost}/api/account/userconfig/`, payload);
        opts.setResult && opts.setResult()
        setSuccessMessage && setSuccessMessage("설정이 저장되었습니다.");
    } catch (error: any) {
        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        if (setErrorMessage) {
            setErrorMessage(errorMessage)
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function retrieveUserProfile(opts: {
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void,
    setResult?: (arg: any) => void
}) {
    const { setErrorMessage, setResult } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/account/userprofile/`);
        if (response.data && setResult) {
            setResult(response.data);
        }
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function updateUserProfile(opts: {
    payload: {
        bus_name?: string;
        phone_number?: string;
        bus_num?: string;
        bus_started_at?: string;
        bus_address?: string;
        rep_name?: string;
        compare_sites_disabled?: Partial<Record<'danawa' | 'enuri' | 'naver', true>>;
    },
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setSuccessMessage?: (arg: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { payload, setErrorMessage, setProcessMessage, setSuccessMessage, setResult } = opts;
    try {
        setProcessMessage && setProcessMessage('저장 중입니다...');
        const response = await apiWithTimeout.normal.patch(`${apiHost}/api/account/userprofile/`, payload);
        setResult && setResult(response.data);
        setSuccessMessage && setSuccessMessage('프로필이 저장되었습니다.');
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    } finally {
        setProcessMessage && setProcessMessage(null);
    }
}

export async function updateUserPassword(opts: {
    payload: {
        current_password: string;
        new_password: string;
        new_password_confirm: string;
    },
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setSuccessMessage?: (arg: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { payload, setErrorMessage, setProcessMessage, setSuccessMessage, setResult } = opts;
    try {
        setProcessMessage && setProcessMessage('변경 중입니다...');
        const response = await apiWithTimeout.normal.post(`${apiHost}/api/account/password/`, payload);
        setResult && setResult(response.data);
        setSuccessMessage && setSuccessMessage('비밀번호가 변경되었습니다.');
    } catch (error: any) {
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    } finally {
        setProcessMessage && setProcessMessage(null);
    }
}
